import { Alert, Button, Group, Modal, Select, Stack, Text, Textarea, Title } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import { httpsCallable } from "firebase/functions";
import { useState } from "react";
import { useFunctions } from "../../helpers/firebaseContext";
import { BaseResult, CramRating } from "../../types";

const CRAM_RATINGS: CramRating[] = ["Low", "Medium", "High", "Prohibited"];

interface CramOverrideModalProps {
    // "apply" sets or changes the pinned rating, "remove" hands it back to the
    // engine. The parent mounts this only while one of the two is in progress,
    // so the draft below never needs resetting.
    mode: "apply" | "remove";
    onClose: () => void;
    customerID: string;
    kycID: string;
    // What the engine currently says, and what is pinned today (if anything).
    engineRating?: CramRating;
    overriddenRating?: CramRating;
}

/**
 * CRAM rating override, for the CRAM team only (the callable enforces the
 * `cram` claim server-side).
 *
 * Its own component rather than the page's shared confirm dialog because
 * applying an override needs a rating selector alongside the mandatory reason —
 * and because keeping the draft here means the KYC page holds nothing but which
 * mode is open.
 */
function CramOverrideModal({
    mode,
    onClose,
    customerID,
    kycID,
    engineRating,
    overriddenRating,
}: CramOverrideModalProps) {
    const functions = useFunctions();
    functions.region = "europe-west1";
    const cramRiskOverride = httpsCallable<
        { customerID: string; kycID: string; rating: CramRating | null; reason: string },
        BaseResult
    >(functions, "cramRiskOverride");

    const removing = mode === "remove";
    const [rating, setRating] = useState<string | null>(
        overriddenRating ?? engineRating ?? null,
    );
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        setError(null);
        setSaving(true);
        try {
            await cramRiskOverride({
                customerID,
                kycID,
                rating: removing ? null : (rating as CramRating),
                reason: reason.trim(),
            });
            showNotification({
                color: "green",
                title: "Done",
                message: removing ? "CRAM override removed." : "CRAM rating overridden.",
            });
            onClose();
        } catch (e: any) {
            setError(e.message);
            setSaving(false);
        }
    };

    return (
        <Modal
            opened
            onClose={onClose}
            title={
                <Title order={3}>
                    {removing ? "Remove CRAM rating override" : "Override CRAM rating"}
                </Title>
            }
        >
            <Stack>
                {removing ? (
                    <Text fw={300}>
                        The rating goes back to the engine's current value
                        {engineRating ? ` (${engineRating})` : ""}. This affects the risk tag,
                        the KYC expiry window and the approval gate.
                    </Text>
                ) : (
                    <>
                        <Text fw={300}>
                            The chosen rating replaces the engine's for this KYC and stays in
                            place through every recalculation until it is removed. The engine
                            keeps running underneath, so the score and breakdown stay live.
                        </Text>
                        <Select
                            label="Rating"
                            data={CRAM_RATINGS}
                            value={rating}
                            onChange={setRating}
                            allowDeselect={false}
                        />
                    </>
                )}
                <Textarea
                    label="Reason"
                    description="Recorded on the KYC audit trail. Required."
                    autosize
                    minRows={2}
                    value={reason}
                    onChange={(e) => setReason(e.currentTarget.value)}
                />
                <Group>
                    <Button
                        color={removing ? "red" : "orange"}
                        loading={saving}
                        disabled={!reason.trim() || (!removing && !rating)}
                        onClick={submit}
                    >
                        {removing ? "Remove override" : "Apply override"}
                    </Button>
                </Group>
                {error && (
                    <Alert icon={<IconAlertCircle size={16} />} title="Whoops!" color="red">
                        {error}
                    </Alert>
                )}
            </Stack>
        </Modal>
    );
}

export default CramOverrideModal;
